import { useState } from "react";
import { Link } from "react-router-dom";
import ChartTypeIcon from "../../components/ChartTypeIcon";
import { useClientBranding } from "../../providers/ClientBrandingProvider";
import { WIDGET_CHART_TYPES } from "../../utils/chartTypeRegistry";
import postgresqlLogo from "../../assets/images/adaptors/postgresql.svg";
import mysqlLogo from "../../assets/images/adaptors/mysql.svg";
import mongodbLogo from "../../assets/images/adaptors/mongodb.svg";
import trinoLogo from "../../assets/images/adaptors/trino.svg";
import clickhouseLogo from "../../assets/images/adaptors/clickhouse.svg";
import hiveLogo from "../../assets/images/adaptors/apachehive.svg";
import sparkLogo from "../../assets/images/adaptors/apachespark.svg";
import databricksLogo from "../../assets/images/adaptors/databricks.svg";
import snowflakeLogo from "../../assets/images/adaptors/snowflake.svg";
import bigqueryLogo from "../../assets/images/adaptors/googlebigquery.svg";
import redshiftLogo from "../../assets/images/adaptors/amazonredshift.svg";
import opensearchLogo from "../../assets/images/adaptors/opensearch.svg";
import ciscoLogo from "../../assets/images/vpn/cisco.svg";
import fortinetLogo from "../../assets/images/vpn/fortinet.svg";
import globalProtectLogo from "../../assets/images/vpn/paloaltonetworks.svg";
import ivantiLogo from "../../assets/images/vpn/ivanti.svg";
import juniperLogo from "../../assets/images/vpn/junipernetworks.svg";
import arrayLogo from "../../assets/images/vpn/arraynetworks.svg";
import f5Logo from "../../assets/images/vpn/f5.svg";
import "./landing.css";

type BrandItem = {
  id: string;
  name: string;
  logo?: string;
  kind?: "logo" | "chart";
};

const ADAPTORS: BrandItem[] = [
  { id: "opensearch", name: "OpenSearch", logo: opensearchLogo },
  { id: "postgresql", name: "PostgreSQL", logo: postgresqlLogo },
  { id: "mysql", name: "MySQL", logo: mysqlLogo },
  { id: "mongodb", name: "MongoDB", logo: mongodbLogo },
  { id: "trino", name: "Trino", logo: trinoLogo },
  { id: "clickhouse", name: "ClickHouse", logo: clickhouseLogo },
  { id: "hive", name: "Apache Hive", logo: hiveLogo },
  { id: "spark", name: "Spark SQL", logo: sparkLogo },
  { id: "databricks", name: "Databricks", logo: databricksLogo },
  { id: "snowflake", name: "Snowflake", logo: snowflakeLogo },
  { id: "bigquery", name: "BigQuery", logo: bigqueryLogo },
  { id: "redshift", name: "Redshift", logo: redshiftLogo },
];

const VPN_PROTOCOLS: BrandItem[] = [
  { id: "anyconnect", name: "Cisco AnyConnect", logo: ciscoLogo },
  { id: "fortinet", name: "Fortinet SSL VPN", logo: fortinetLogo },
  { id: "globalprotect", name: "GlobalProtect", logo: globalProtectLogo },
  { id: "pulse", name: "Pulse / Ivanti", logo: ivantiLogo },
  { id: "juniper", name: "Juniper NC", logo: juniperLogo },
  { id: "array", name: "Array Networks", logo: arrayLogo },
  { id: "f5", name: "F5 BIG-IP", logo: f5Logo },
];

const CHART_TYPES: BrandItem[] = WIDGET_CHART_TYPES.map((chart) => ({
  id: chart.id,
  name: chart.labelEn,
  kind: "chart",
}));

const FEATURES = [
  {
    title: "Ask in plain language",
    body: "Chat across indices, warehouses, MongoDB, and OpenAPI—tables, charts, HTML.",
  },
  {
    title: "Visual BI dashboards",
    body: "Drag fields onto charts and slicers; export PDF, print, or Excel.",
  },
  {
    title: "Live multi-source adaptors",
    body: "OLTP and warehouses run live with scan guards—no forced materialize.",
  },
  {
    title: "Prep, discover, monitor",
    body: "Pipelines, schema guides, anomalies, and scheduled insight jobs.",
  },
];

function BrandMarquee({
  items,
  label,
  speedClass,
}: {
  items: BrandItem[];
  label: string;
  speedClass?: string;
}) {
  return (
    <div
      className={`gar-marketing__marquee${speedClass ? ` ${speedClass}` : ""}`}
      tabIndex={0}
      aria-label={label}
    >
      <ul className="gar-marketing__marquee-track">
        {[...items, ...items].map((item, index) => (
          <li
            key={`${item.id}-${index}`}
            className="gar-marketing__adaptor"
            aria-hidden={index >= items.length}
          >
            <span className="gar-marketing__adaptor-mark">
              {item.kind === "chart" ? (
                <ChartTypeIcon typeId={item.id} size={40} />
              ) : item.logo ? (
                <img src={item.logo} alt="" width={40} height={40} />
              ) : null}
            </span>
            <span className="gar-marketing__adaptor-name">{item.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

type ConnectorTab = "adaptors" | "vpn" | "charts";

const CONNECTOR_TABS: { id: ConnectorTab; label: string }[] = [
  { id: "adaptors", label: "Adaptors" },
  { id: "vpn", label: "VPN" },
  { id: "charts", label: "Charts" },
];

export default function LandingPage() {
  const { brandName, brandMark, brandImageLight } = useClientBranding();
  const [connectorTab, setConnectorTab] = useState<ConnectorTab>("adaptors");

  return (
    <div className="gar-marketing">
      <header className="gar-marketing__nav">
        <Link to="/" className="gar-marketing__brand" aria-label={brandName || "Connect AI"}>
          <img src={brandMark || brandImageLight} alt="" />
          <span className="gar-marketing__brand-name">
            <span className="gar-marketing__brand-connect">Connect</span>
            <span className="gar-marketing__brand-ai">AI</span>
          </span>
        </Link>
        <nav className="gar-marketing__nav-actions">
          <Link className="gar-marketing__link" to="/how-it-works">
            How it works
          </Link>
          <Link className="gar-marketing__btn gar-marketing__btn--ghost" to="/login">
            Sign in
          </Link>
          <Link className="gar-marketing__btn gar-marketing__btn--primary" to="/register">
            Create account
          </Link>
        </nav>
      </header>

      <main className="gar-marketing__shell">
        <section className="gar-marketing__overview" aria-labelledby="gar-hero-title">
          <div className="gar-marketing__hero">
            <h1 id="gar-hero-title">From warehouses to executive dashboards</h1>
            <p>
              Connect Postgres to Snowflake, ask in plain language, and design interactive BI boards
              your leaders can trust—live from the systems they already run.
            </p>
            <div className="gar-marketing__cta-row">
              <Link className="gar-marketing__btn gar-marketing__btn--primary" to="/register">
                Start free
              </Link>
              <Link className="gar-marketing__btn gar-marketing__btn--ghost" to="/how-it-works">
                See how it fits your stack
              </Link>
            </div>
          </div>

          <ul className="gar-marketing__feature-list" aria-label="Product capabilities">
            {FEATURES.map((feature) => (
              <li key={feature.title} className="gar-marketing__feature">
                <h2>{feature.title}</h2>
                <p>{feature.body}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="gar-marketing__connectors" aria-label="Supported connectors">
          <div className="gar-marketing__tabs" role="tablist" aria-label="Connector types">
            {CONNECTOR_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`gar-tab-${tab.id}`}
                aria-controls={`gar-panel-${tab.id}`}
                aria-selected={connectorTab === tab.id}
                className={`gar-marketing__tab${connectorTab === tab.id ? " is-active" : ""}`}
                onClick={() => setConnectorTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            id="gar-panel-adaptors"
            role="tabpanel"
            aria-labelledby="gar-tab-adaptors"
            hidden={connectorTab !== "adaptors"}
          >
            <BrandMarquee items={ADAPTORS} label="Supported data adaptors, auto-scrolling row" />
          </div>
          <div
            id="gar-panel-vpn"
            role="tabpanel"
            aria-labelledby="gar-tab-vpn"
            hidden={connectorTab !== "vpn"}
          >
            <BrandMarquee
              items={VPN_PROTOCOLS}
              label="Supported VPN protocols, auto-scrolling row"
              speedClass="gar-marketing__marquee--slow"
            />
          </div>
          <div
            id="gar-panel-charts"
            role="tabpanel"
            aria-labelledby="gar-tab-charts"
            hidden={connectorTab !== "charts"}
          >
            <BrandMarquee
              items={CHART_TYPES}
              label="Supported Apache ECharts chart types, auto-scrolling row"
              speedClass="gar-marketing__marquee--charts"
            />
          </div>
        </section>
      </main>

      <footer className="gar-marketing__footer">
        Already have a workspace? <Link to="/login">Sign in</Link>
      </footer>
    </div>
  );
}
